// The blueprint's OGS cluster is load-bearing: the engine presents splats only
// while the renderer's full input chain is intact. Deleting/disabling the
// renderer node, unplugging either of its inputs, or breaking the upstream OGS
// avatar node/edge stops the render — the graph IS the pipeline.
// Active = GraphEvaluator's plugin-tick convention (enabled ?? true, weight ?? 1).

const OGS_PREFIX = 'plugin.ogs.'
const BRANCH_TYPE = 'plugin.ogs.branch'

function nodeActive(node) {
  if (!node) return false
  return (node.data?.enabled ?? true) && (node.data?.weight ?? 1) > 0
}

// Does the blueprint contain an OGS renderer node at all (wired or not)? This is
// the difference between "we're just three.js" and "the splat renderer exists
// but has nothing to draw yet".
export function hasRendererNode(nodes) {
  return Array.isArray(nodes) && nodes.some((n) => n.type === 'plugin.ogs.renderer')
}

// Collapse the two gate signals into the render phase OGSModel drives its
// init/render off of:
//   'three'     — no renderer node → the ordinary three scene only, no OGS work.
//   'created'   — renderer node present but its input chain isn't fully wired →
//                 the splat mesh is created and idle (nothing rendered).
//   'rendering' — the full renderer chain is active → run the inference pipeline.
export function resolveRenderPhase(nodes, edges) {
  if (!hasRendererNode(nodes)) return 'three'
  return isRendererChainActive(nodes, edges) ? 'rendering' : 'created'
}

export function isRendererChainActive(nodes, edges) {
  if (!Array.isArray(nodes)) return false
  const renderer = nodes.find((n) => n.type === 'plugin.ogs.renderer')
  if (!renderer) return false

  const byId = new Map(nodes.map((n) => [n.id, n]))
  const inbound = new Map()
  for (const e of edges ?? []) {
    if (!inbound.has(e.target)) inbound.set(e.target, [])
    inbound.get(e.target).push(e)
  }

  const memo = new Map() // id -> boolean | 'pending' ('pending' breaks cycles in malformed graphs)
  const satisfied = (node) => {
    if (!node) return false
    // A branch node's `enabled` is a REGION toggle (branchRegionStates), never a
    // broken source — the head sits on the REQUIRED pins, so standing the chain
    // down for it would take every other region with it, which is the one thing
    // the toggle exists to avoid. A STARVED branch is still a broken graph.
    if (node.type !== BRANCH_TYPE && !nodeActive(node)) return false
    // Non-OGS sources (blend/liveLink/... feeding the encoder's rig) are the
    // face chain's domain — GraphEvaluator owns their semantics.
    if (!node.type?.startsWith(OGS_PREFIX)) return true
    const m = memo.get(node.id)
    if (m === 'pending') return true
    if (m !== undefined) return m
    memo.set(node.id, 'pending')
    const ok = evaluate(node)
    memo.set(node.id, ok)
    return ok
  }
  const evaluate = (node) => {
    const ins = inbound.get(node.id) ?? []
    const required = REQUIRED_PINS[node.type] ?? []
    const optional = OPTIONAL_PINS[node.type] ?? []
    const pins = new Set(ins.map((e) => e.targetHandle))
    if (!required.every((p) => pins.has(p))) return false
    // Optional side-inputs (a secondary region, the eyes gate) never gate the
    // core geom/appr chain — disabling one must not blank the face splat.
    return ins
      .filter((e) => !optional.includes(e.targetHandle))
      .every((e) => satisfied(byId.get(e.source)))
  }

  return satisfied(renderer)
}

// Pins that must be wired for the stage to run, mirroring the TAs algorithm.
// The avatar node folds encoder+decoders+plücker: its `rig` input is the data
// dependency that can starve the whole decode (the camera's rays are internal).
// Legacy decoder/encoder types are intentionally omitted — a stale authored
// cluster stays visible (this table is permissive for unlisted types).
const REQUIRED_PINS = {
  'plugin.ogs.renderer': ['geom_uv', 'color_uv'],
  'plugin.ogs.avatar': ['rig'],
  'plugin.ogs.branch': ['rig'],
  'plugin.ogs.pluckerCamera': [],
}

// Pins that may be wired but must NOT gate the stage — a missing/disabled
// source here leaves the core render intact (a secondary region feeds the same
// rasterizer call but a broken secondary chain must never blank the face; the
// eye branch presents its own SplatMesh, so its pins gate the eyes alone).
// One entry per secondary region, named as
// graph/branchPorts.ts names it. Keep in step with the dataIn pins in
// plugins/ogs-renderer/plugin.json — a pin missing here is counted as a
// required input, which turns a wired branch into a whole-renderer stall
// rather than an optional region (tests/unit/blueprintPluginPins.test.mjs).
const OPTIONAL_PINS = {
  'plugin.ogs.renderer': [
    'body_geom_uv', 'body_color_uv',
    'teeth_geom_uv', 'teeth_color_uv',
    'clothes_geom_uv', 'clothes_color_uv',
    'hair_geom_uv', 'hair_color_uv',
    'eyes_geom_uv', 'eyes_color_uv',
  ],
}

// Which multi-region branches are live: a plugin.ogs.branch node is active when
// it's enabled, its rig input chain is satisfied, and its outputs reach an
// active renderer — the head via the required geom_uv/color_uv pins, every
// other region via its own optional pair. Returns { region: true } keyed
// by the node's `region` config (default 'head') — present only for a LIVE
// region, so an absent key means "off OR never named". The engine needs those
// told apart and reads branchRegionStates, which is built on this.
export function activeBranchRegions(nodes, edges) {
  const out = {}
  if (!Array.isArray(nodes)) return out
  const renderer = nodes.find((n) => n.type === 'plugin.ogs.renderer')
  if (!nodeActive(renderer)) return out
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const rendererPins = new Set([...REQUIRED_PINS['plugin.ogs.renderer'], ...OPTIONAL_PINS['plugin.ogs.renderer']])
  for (const e of edges ?? []) {
    if (e.target !== renderer.id || !rendererPins.has(e.targetHandle)) continue
    const src = byId.get(e.source)
    if (src?.type !== BRANCH_TYPE || !nodeActive(src)) continue
    const rigWired = (edges ?? []).some((r) => r.target === src.id && r.targetHandle === 'rig' && nodeActive(byId.get(r.source)))
    if (!rigWired) continue
    out[src.data?.region ?? 'head'] = true
  }
  return out
}

// On/off for every region the graph DECLARES a branch node for: true when that
// node is enabled and its chain reaches the renderer, false when the author
// switched it off (or unwired/starved it). A region with no branch node is
// ABSENT rather than false — the engine renders it, so a blueprint that never
// names a region can't silently turn it off. `states[region] ?? true` is the
// read; activeBranchRegions can't answer it, since "off" and "never mentioned"
// look identical there.
export function branchRegionStates(nodes, edges) {
  const out = {}
  if (!Array.isArray(nodes)) return out
  const active = activeBranchRegions(nodes, edges)
  for (const n of nodes) {
    if (n?.type !== BRANCH_TYPE) continue
    const region = n.data?.region ?? 'head'
    // Two nodes on one region: either one live keeps the region on.
    out[region] = (out[region] ?? false) || !!active[region]
  }
  return out
}

// The plugin.ogs.branch node for a region, or null. Config a region needs beyond
// on/off lives on the node (the eye branch's `lookAtCamera`), and
// activeBranchRegions can only answer on/off.
export function branchNode(nodes, region) {
  if (!Array.isArray(nodes)) return null
  return nodes.find((n) => n.type === BRANCH_TYPE && (n.data?.region ?? 'head') === region) ?? null
}

/**
 * createRenderBlockDetector — decides when a splat character's boot is
 * DEAD-STUCK rather than merely slow (ENG-991). eyeline sat on "Loading
 * avatar…" forever because its saved blueprint had NO renderer chain: the
 * phase resolved 'three', the pipeline never started, onReady never fired,
 * and the only diagnostic was a console.info. This turns that settled state
 * into a signal App can render.
 *
 * Rules, and why each exists:
 * - Only NON-'rendering' phases can block ('three' = no renderer node,
 *   'created' = node present but chain not wired) — in both, the pipeline
 *   never starts and nothing will ever clear the overlay.
 * - Debounced (`delayMs`): the phase is legitimately 'three' for a moment at
 *   boot while the DB blueprint is still in flight (observed in the eyeline
 *   console: the "[isaac] no OGS renderer node" line fired BEFORE
 *   "Loaded from DB"). A healthy character's chain arrives and cancels the
 *   timer; only a phase that SETTLES blocked fires.
 * - One-shot per stuck stretch: fires 'blocked' once, then stays quiet until
 *   the phase leaves and re-enters, so the App callback isn't spammed.
 * - Leaving a blocked phase after reporting returns 'unblocked' — an author
 *   wiring the chain in the editor recovers the UI live, no reload.
 * - Time is INJECTED (now arguments), never read — same unit-test discipline
 *   as dampFactor/momentum in cameraController.
 *
 * OGSModel stops feeding the detector once the pipeline has started
 * (initStarted): after first render, dropping to 'three' is the author
 * deliberately disconnecting the chain — a legitimate editing state, not a
 * stuck boot.
 */
export function createRenderBlockDetector({ delayMs = 8000 } = {}) {
  let since = null      // when the current non-rendering stretch began
  let reported = false  // 'blocked' already emitted for this stretch
  return {
    /** Feed every phase change (and the initial phase). Returns 'unblocked'
     *  when a previously-reported block clears, else null. */
    notePhase(phase, now) {
      if (phase === 'rendering') {
        const wasReported = reported
        since = null
        reported = false
        return wasReported ? 'unblocked' : null
      }
      if (since === null) since = now
      return null
    },
    /** Poll on a timer. Returns 'blocked' exactly once per stuck stretch. */
    poll(now) {
      if (since === null || reported) return null
      if (now - since < delayMs) return null
      reported = true
      return 'blocked'
    },
  }
}
